import React, { useEffect, useState } from "react";
import AttachmentIcon from "assets/medication/attachments.svg";
import NoteIcon from "assets/medication/documents.svg";
import ParticipantCard from "pages/Rostering/UpdatedTimeSheet/components/Card/ParticipantCard";
import { apiPostRequest } from "helpers/Requests";
import { URL_ROSTERING_GET_ALL_MEDICATION_LIST } from "store/services/URL";
import SelectOption from "components/common/Select/Select";
import { useSelector } from "react-redux";
import { getParticipantsList, getSpRosteringSetting, getSpSetting } from "store/selector/sp.selector";
import { getParticipantLabel } from "Constants/constant";
import { TimesheetMedicationTabSkeleton } from "../../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";
import moment from "moment";
import { convertTo12HourFormat } from "Utils/utils";
import { TimesheetMedStatusPill } from "../../../components/TimesheetRevamp/TimesheetTabPrimitives";
import "../../../components/TimesheetRevamp/timesheet-detail-revamp.css";

const formatUOM = (uom) => {
  const uomMap = {
    tablet: "Tablet",
    capsule: "Capsule",
    half_tablet: "Half tablet",
    quarter_tablet: "Quarter tablet",
    milliliter: "mL",
    teaspoon: "tsp",
    tablespoon: "tbsp.",
    drops: "Drop(s)",
    vial: "Vial",
    bottle: "Bottle",
    units: "Unit(s)",
    patch: "Patch",
    puff: "Puff",
    sachet: "Sachet",
  };
  return uomMap[uom] || uom;
};

const getAttachmentCount = (attachments) => {
  if (!attachments) return 0;
  try {
    const parsed = JSON.parse(attachments);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
};

const TaskList = ({ shiftDetails }) => {
  const participantList = useSelector(getParticipantsList);
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const spSetting = useSelector(getSpSetting);
  const careSector = spRosteringSetting?.care_sector ?? spSetting?.care_sector;
  const participantLabel = getParticipantLabel(careSector, false);
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  useEffect(() => {
    if (shiftDetails?.participants?.length > 0) {
      const participantsData = shiftDetails?.participants || [];

      const modifiedParticipants = [
        ...participantsData.map((participant) => ({
          ...participant,
          label: `${participant.first_name} ${participant.last_name}`,
          value: participant.id,
        })),
      ];

      setParticipants(modifiedParticipants);
      setSelectedParticipant(modifiedParticipants[0]);
    }
  }, [participantList, shiftDetails?.participants]);

  const [medicationList, setMedicationList] = useState([]);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (selectedParticipant) {
      getMedicationList(selectedParticipant.value);
    }
  }, [shiftDetails?.shift_id, selectedParticipant]);

  const getMedicationList = (Id = "all") => {
    if (!shiftDetails?.shift_id) return [];
    setLoader(true);
    const payload = {
      shift_id: shiftDetails?.shift_id,
      pt_id: Id,
      status: "any",
    };
    apiPostRequest(URL_ROSTERING_GET_ALL_MEDICATION_LIST, payload)
      .then((response) => {
        if (response?.status === 200) {
          const data = response?.data?.data || [];
          const modified = [
            ...data?.normal_medication,
            ...data?.prn_medication,
          ];
          setMedicationList(modified || []);
        }
      })
      .catch((error) => {
        console.error("Error fetching medication list:", error);
        return [];
      })
      .finally(() => {
        setLoader(false);
      });
  };

  return (
    <div className="timesheet-tab-content td-medication-tab min-h-0">
      <div className="td-med-filter-row">
        <label className="td-med-filter-label" htmlFor="med-participant-filter">
          Filter by {participantLabel.toLowerCase()}
        </label>
        <SelectOption
          cssClass="td-med-filter-select"
          data={participants}
          selectedOption={selectedParticipant}
          handleChange={setSelectedParticipant}
        />
      </div>

      {loader ? (
        <TimesheetMedicationTabSkeleton />
      ) : medicationList?.length > 0 ? (
        <div className="td-med-table-wrap overflow-x-auto">
          <table className="td-med-table min-w-[760px]">
            <thead>
              <tr>
                <th>{participantLabel}</th>
                <th>Medication</th>
                <th>Event</th>
                <th>Status</th>
                <th>Date</th>
                <th>Notes</th>
                <th>Attachment</th>
              </tr>
            </thead>
            <tbody>
              {medicationList.map((task, index) => {
                const attachmentCount = getAttachmentCount(task?.attachments);

                return (
                  <tr key={index}>
                    <td>
                      <ParticipantCard item={selectedParticipant} />
                    </td>
                    <td>
                      <div className="font-semibold text-[#0F172A]">
                        {task?.medicine_name}
                      </div>
                      {task?.quantity && task?.unit_of_measure && (
                        <div className="mt-1 text-xs font-semibold text-[#64748B]">
                          {task.quantity} {formatUOM(task.unit_of_measure)}
                        </div>
                      )}
                      {task?.instructions && (
                        <div className="mt-1 text-xs text-[#64748B]">
                          <span className="font-semibold text-[#0F172A]">
                            Instructions:
                          </span>{" "}
                          {task.instructions}
                        </div>
                      )}
                      {task?.side_effects && (
                        <div className="mt-1 text-xs text-[#FC466C]">
                          <span className="font-semibold">Side Effects:</span>{" "}
                          {task.side_effects}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-[#64748B]">
                      {task?.event_type === "time"
                        ? convertTo12HourFormat(task?.time)
                        : task?.event_type === "prn"
                        ? "PRN"
                        : task?.event_name}
                    </td>
                    <td>
                      <TimesheetMedStatusPill letter={task?.log_status_letter} />
                    </td>
                    <td className="whitespace-nowrap text-[#64748B]">
                      {moment(task?.log_date).format("dddd, Do MMMM YYYY")}
                    </td>
                    <td>
                      {task?.notes && (
                        <span className="td-med-icon-badge">
                          <img src={NoteIcon} alt="notes" className="h-5 w-5" />
                          <span className="td-med-icon-badge-count">1</span>
                        </span>
                      )}
                    </td>
                    <td>
                      {attachmentCount > 0 && (
                        <span className="td-med-icon-badge">
                          <img
                            src={AttachmentIcon}
                            alt="attachment"
                            className="h-5 w-5"
                          />
                          <span className="td-med-icon-badge-count">
                            {attachmentCount}
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="td-med-empty">
          No medication added for this shift.
        </div>
      )}
    </div>
  );
};

export default TaskList;
